import React, { useEffect, useState } from "react";
import {
  FormContainer,
  FormHeader,
  FormField,
  SubmitButton,
  Message,
  ValidationError,
} from "./CreateUserForm.styles";
import CpfInputField from "../FormField/CpfInputField";
import api from "../../services/api";
import { HttpStatusCode } from "../../enums/HttpStatusCode";

const CreateUserForm = () => {
  const [formValues, setFormValues] = useState({
    cpf: "",
    nome: "",
    email: "",
    perfil: "",
    municipio: "",
    regional: "",
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [perfis, setPerfis] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [regionais, setRegionais] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleApiError = (error) => {
    if (error.response?.status === HttpStatusCode.UNPROCESSABLE_ENTITY && Array.isArray(error.response.data.detail)) {
      const fieldErrors = {};
      error.response.data.detail.forEach((err) => {
        const fieldName = err.loc?.[1]; // loc: ['body', 'campo']
        let message = err.msg;
        // Remove 'Value error, ' prefix if present
        if (typeof message === 'string' && message.startsWith('Value error, ')) {
          message = message.replace('Value error, ', '');
        }
        if (fieldName) fieldErrors[fieldName] = message;
      });
      setValidationErrors(fieldErrors);
    } else {
      const msg = error.response?.data?.detail || "Erro ao enviar o formulário.";
      setErrorMessage(msg);
    }
  };

  useEffect(() => {
    const fetchPerfis = async () => {
      try {
        const response = await api.get("/users/perfis/list");
        setPerfis(response.data);
      } catch (error) {
        console.error("Erro ao buscar perfis:", error);
      }
    };

    const fetchMunicipios = async () => {
      try {
        const response = await api.get("/municipios/list", {
          params: { page: 1, size: 853 },
        });
        setMunicipios(response.data);
        //console.log(response.data)
      } catch (error) {
        console.error("Erro ao buscar municípios:", error);
      }
    };

    const fetchRegionais = async () => {
      try {
        const response = await api.get("/regionais/list", {
          params: { page: 1, size: 28 },
        });
        setRegionais(response.data);
        // console.log(response.data);
      } catch (error) {
        console.error("Erro ao buscar regionais:", error);
      }
    };

    fetchPerfis();
    fetchMunicipios();
    fetchRegionais();

  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccessMessage("");
    setErrorMessage("");
    setValidationErrors({});

    const sanitizedCpf = formValues.cpf ? formValues.cpf.replace(/[.-]/g, "") : "";
    const sanitizedData = {
      ...formValues,
      cpf: sanitizedCpf,
      municipio: formValues.municipio ? parseInt(formValues.municipio, 10) : 0,
      regional:
        !formValues.municipio || !formValues.regional
          ? 0
          : parseInt(formValues.regional, 10),
    };

    try {
      await api.post("/users/create", sanitizedData);
      const sucessMessage = "Usuário criado com sucesso!";
      setSuccessMessage(sucessMessage);
      setFormValues({
        cpf: "",
        nome: "",
        email: "",
        perfil: "",
        municipio: "",
        regional: "",
      });
      setValidationErrors({});
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
      <FormContainer>
        <FormHeader>Adicionar Usuário</FormHeader>
        <form onSubmit={handleSubmit}>
          <FormField>
            <CpfInputField
              value={formValues.cpf}
              onChange={handleChange}
              error={validationErrors.cpf}
            />
          </FormField>

          <FormField>
            <label htmlFor="nome">Nome de Usuário:</label>
            <input
              type="text"
              id="nome"
              name="nome"
              value={formValues.nome}
              onChange={handleChange}
            />
            {validationErrors.nome && <ValidationError>{validationErrors.nome}</ValidationError>}
          </FormField>

          <FormField>
            <label htmlFor="email">E-mail:</label>
            <input
              type="text"
              id="email"
              name="email"
              value={formValues.email}
              onChange={handleChange}
            />
            {validationErrors.email && <ValidationError>{validationErrors.email}</ValidationError>}
          </FormField>

          <FormField>
            <label htmlFor="perfil">Perfil:</label>
            <select
              id="perfil"
              name="perfil"
              value={formValues.perfil}
              onChange={handleChange}
            >
              <option value="">Selecione...</option>
              {perfis.map((perfil) => (
                <option key={perfil.id} value={perfil.id}>
                  {perfil.nome}
                </option>
              ))}
            </select>
            {validationErrors.perfil && <ValidationError>{validationErrors.perfil}</ValidationError>}
          </FormField>

          <FormField>
            <label htmlFor="municipio">Município:</label>
            <select
              id="municipio"
              name="municipio"
              value={formValues.municipio}
              onChange={(e) => {
                const codMunicipio = e.target.value;
                const municipioSelecionado = municipios.find(m => m.cod_ibge.toString() === codMunicipio);
                const codRegional = municipioSelecionado?.regional?.cod_urs || "";
                setFormValues((prev) => ({
                  ...prev,
                  municipio: codMunicipio,
                  regional: codRegional, // já define a regional vinculada ao município
                  }));
                  setValidationErrors((prev) => ({
                    ...prev,
                    municipio: ""
                  }));
                }}
            >
              {municipios.length === 0 ? (
                <option disabled>Carregando municípios...</option>
              ) : (
                <>
                  <option value="">Selecione...</option>
                  {municipios.map((municipio) => (
                    <option key={municipio.cod_ibge} value={municipio.cod_ibge}>
                      {municipio.nome}
                    </option>
                  ))}
                </>
              )}
            </select>
            {validationErrors.municipio && (
              <ValidationError>{validationErrors.municipio}</ValidationError>
            )}
          </FormField>

          <FormField>
            <label htmlFor="regional">Regional:</label>
            <select
              id="regional"
              name="regional"
              value={formValues.regional}
              onChange={handleChange}
              disabled // opcional, já que será preenchido pelo município
            >
              {regionais.length === 0 ? (
                <option disabled>Carregando regionais...</option>
              ) : (
                <>
                  <option value="">Selecione...</option>
                  {regionais.map((regional) => (
                    <option key={regional.cod_urs} value={regional.cod_urs}>
                      {regional.nome}
                    </option>
                  ))}
                </>
              )}
            </select>
            {validationErrors.regional && (
              <ValidationError>{validationErrors.regional}</ValidationError>
            )}
          </FormField>

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Enviando..." : "Enviar"}
          </SubmitButton>
        </form>

        {successMessage && <Message>{successMessage}</Message>}
        {errorMessage && <Message isError>{errorMessage}</Message>}
      </FormContainer>
  );
};

export default CreateUserForm;
